<script lang="ts">

    import Modal from "@/components/Modal.svelte";
    import RelatedPolymorphic from "@/components/Inputs/RelatedPolymorphic.svelte";
    import {Button} from "@/components/ui/button";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import type Blocks from "@/schemas/public/Blocks";
    import type {Field} from "@/types/types";
    import {toast} from "svelte-sonner";
    import {useForm} from "@/lib/precognition.svelte";
    import VehicleTable from "@/components/vehicles/VehicleTable.svelte";
    import EmployeeTable from "@/components/employees/EmployeeTable.svelte";
    import RelatedSingle from "@/components/Inputs/RelatedSingle.svelte";
    import CustomerTable from "@/components/customers/CustomerTable.svelte";
    import FileUpload from "@/components/Inputs/FileUpload.svelte";
    import JobTable from "@/components/jobs/JobTable.svelte";
    import Checkbox from "@/components/Inputs/Checkbox.svelte";
    import Select from "@/components/Inputs/Select.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import { parseFieldOptions } from '@/lib/fieldUtils';

    type Props = {
        blocks: (Blocks & { fields: Field[] })[];
        show: boolean;
        relatedRecordType?: string | null;
        relatedRecordId?: string | null;
        relatedRecordLabel? : string | null;
    };

    let {
        blocks,
        show = $bindable(),
        relatedRecordType,
        relatedRecordId,
        relatedRecordLabel
    }: Props = $props();

    let createFormInner: Record<string, any> = {}
    for (const block of blocks) {
        for (const field of block.fields) {
            createFormInner[field.column_name] = null;
        }
    }
    createFormInner['related_record_type'] = relatedRecordType ?? null;
    createFormInner['related_record_id'] = relatedRecordId ?? null;
    createFormInner['file'] = null;
    let createForm = $state(useForm(createFormInner, 'post', route('documents.store')));

    // let isSelectRelatedOpen = $state(false);
    // let relatedRecordLabel: string | null = $state(null);

    function close() {
        show = false;
    }

    function submit(e) {
        $createForm.processing = true;
        e.preventDefault();
        $createForm.post(route('documents.store'), {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                close();
                $createForm.reset();
                toast.success("Document created successfully!");
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error("Error creating document. " + message as string);
                }
            }
        });
    }

    let isSelectRelatedOpen = $state(false);
    let relatedModule: string | null = $state(null);
    let relatedLabel: string | null = $state(relatedRecordLabel ?? null);

    const polymorphicFieldConfigs = {
        Vehicle: {
            component: VehicleTable,
            props: {
                view: "related",
                onRowClick: (row) => {
                    $createForm.related_record_id = row?.id ?? null;
                    $createForm.related_record_type = 'App\\Models\\Vehicle';
                    relatedLabel = row?.registration ?? "";
                    isSelectRelatedOpen = false;
                }
            }
        },
        Employee: {
            component: EmployeeTable,
            props: {
                view: "related",
                onRowClick: (row) => {
                    $createForm.related_record_id = row?.id ?? null;
                    $createForm.related_record_type = 'App\\Models\\Employee';
                    relatedLabel = row?.name ?? "";
                    isSelectRelatedOpen = false;
                }
            }
        },
        Job: {
            component: JobTable,
            props: {
                view: "related",
                onRowClick: (row) => {
                    $createForm.related_record_id = row?.id ?? null;
                    $createForm.related_record_type = 'App\\Models\\Job';
                    relatedLabel = row?.title ?? "";
                    isSelectRelatedOpen = false;
                }
            }
        },
    };
</script>
{#if show}
    <Modal title="Add New Document" onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                {#each blocks as block}
                    <FormBlock label={block.label}>
                        {#each block.fields as field}
                            {#if field.editable}
                                {#if field.ui_type === 'polymorphic'}
                                    <RelatedPolymorphic
                                        label="Related Record"
                                        bind:module={relatedModule}
                                        bind:value={$createForm[field.column_name]}
                                        bind:valLabel={relatedLabel}
                                        error={$createForm.errors[field.column_name]}
                                        bind:isOpen={isSelectRelatedOpen}
                                        children={polymorphicFieldConfigs}
                                    />
                                {:else if field.ui_type === 'file'}
                                    <FileUpload
                                        name="file"
                                        server={null}
                                        allowMultiple={false}
                                        bind:value={$createForm['file']}
                                        label={field.label}
                                        maxFileSize="30MB"
                                    />
                                {:else if field.ui_type === 'checkbox'}
                                    <Checkbox
                                        bind:checked={$createForm[field.column_name]}
                                        label={field.label}
                                        error={$createForm.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'select'}
                                    <Select
                                        label={field.label}
                                        bind:value={$createForm[field.column_name]}
                                        options={parseFieldOptions(field)}
                                        error={$createForm.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'textarea'}
                                    <Textarea
                                        label={field.label}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'datetime'}
                                    <DateTime
                                        label={field.label}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                    />
                                {:else}
                                    <Input
                                        field={field}
                                        bind:value={$createForm[field.column_name]}
                                        error={$createForm.errors[field.column_name]}
                                        onkeydown={() => {}}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <div class="mt-5 flex justify-end gap-2">
                    <Button type="button" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$createForm.processing}>
                        {#if $createForm.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}
